import { useEffect, useState } from "react";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { calculateStartAndEndDates } from "../helpers/businessleaderDashboardHelpers";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Stack } from "@mui/system";
import {
  selectUserData,
  selectUserToken,
} from "../../../../../features/userSlice";
import { CheckPermissions } from "../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { buiDashboardEndPointLogic } from "../../../../../logic/AssetManagement/overview/BusinessLeaderDashboard/buiDashboardEndPointLogic";
import store from "../../../../../store";
import {
  BuiDashboardSelection,
  setBuiDashboardSelection,
} from "../../../../../features/businessLeaderDashboardSlice";

interface BuiLeaderDashBoardFilterProps {
  siteOptions: any;
  getAllDimesnionsForViewOrganizationalSummary: any;
  getAllDashboardSectionsData: any;
  setCollapseAllSections: any;
  onGetDataTriggered: any;
  setHighLevelData: any;
  sethighLevelTargetActualData: any;
  setDataShow: any;
}

function BusinessLeaderDashboardFilter({
  siteOptions,
  getAllDimesnionsForViewOrganizationalSummary,
  getAllDashboardSectionsData,
  setCollapseAllSections,
  onGetDataTriggered,
  setHighLevelData,
  sethighLevelTargetActualData,
  setDataShow,
}: BuiLeaderDashBoardFilterProps) {
  // values fom business leader slice
  const {
    selectedSharedUidBui,
    selectedDimensions,
    selectedType,
    //selectedPeriod,
    selectedInterval,
    selectedAll,
    websites,
  } = useSelector((state: any) => state?.businessLeaderDashBoard);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const [searchParams, setSearchParams] = useSearchParams();

  const dimensionFromUser = useSelector(selectUserData)?.data.dimensions;
  const [viewOrganizationalSummary, setViewOrganizationalSummary] = useState(
    []
  ) as any;
  const [, setAllDimensionOrganization] = useState() as any;

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isValid] = useState(false);

  // values fom business leader slice
  const [savedUid, setSavedUid] = useState([]);
  const [savedDimensions, setSavedDimensions] = useState([]);
  const [savedType, setSavedType] = useState<string>("site");
  const [savedPeriod, setSavedPeriod] = useState<string>();
  const [savedInterval, setSavedInterval] = useState<string>("Month");
  const [savedSelectedAll, setSavedSelectedAll] = useState<boolean>(false);

  const userData = useSelector(selectUserData);

  const [selectedValueRadioFilter, setSelectedValueRadioFilter] =
    useState("Website"); // Default selection

  const isUserHasViewOrganizationalSummary = CheckPermissions(
    // check if user has view_organizational_summary
    userData?.data?.products?.assets,
    "overview",
    "view_organizational_summary"
  );

  const userWebsiteList = siteOptions?.map((site: any) => ({
    label: site?.siteName,
    value: site?.uid,
    dimensions: site.dimensions,
  }));

  const [websiteList, setWebsiteList] = useState(userWebsiteList);

  const dimensionList = userData?.data?.dimensions?.map(
    (dimension: string) => ({
      label: dimension,
      value: dimension,
    })
  );

  const handleChangeRadioFilter = (event: any) => {
    setSelectedValueRadioFilter(event.target.value);
  };

  const handleRadioFilterChange = async (event: any) => {
    const selectedValue = event.target.value;

    const updatedSearchParams = new URLSearchParams(searchParams);
    const newValue = selectedValue;
    // setChecked(newValue);
    setSavedDimensions([]);
    setSavedUid([]);
    setDataShow(false);
    setSavedSelectedAll(false);
    removeStateFromURL(
      ["selectedSharedUids", "selectedDimension", "selectedAll"],
      updatedSearchParams
    );
    if (newValue === "Dimension") {
      updateParamsSelectedType("dimension", updatedSearchParams);
      setSavedType("dimension");
    } else if (newValue === "Website") {
      updateParamsSelectedType("site", updatedSearchParams);
      setSavedType("site");
      setWebsiteList(userWebsiteList);
    } else if (newValue === "Organization") {
      try {
        const sitesData =
          await buiDashboardEndPointLogic.getWebsitesInBuiDashboard(
            oauthTokenHeader
          );
        const sourceSites = Array.isArray(sitesData)
          ? sitesData
          : websites || [];
        const activeSites = sourceSites
          .filter((w: any) => w?.is_active === 1)
          .map((w: any) => ({
            label: w?.ui_site_name || w?.site_name,
            value: w?.shared_uid,
            dimensions: w?.dimensions || [],
          }));

        updateParamsSelectedType("organization", updatedSearchParams);
        setSavedType("organization");
        setSavedSelectedAll(true);
        setWebsiteList(activeSites);
        setSavedUid(activeSites);
        updateParamsSelectedSharedUid(activeSites, updatedSearchParams);
      } catch (err) {
        console.error("Error fetching organization websites", err);
        updateParamsSelectedType("organization", updatedSearchParams);
        setSavedType("organization");
        setSavedSelectedAll(true);
        setWebsiteList(userWebsiteList);
      }
    }
  };

  const renderSnackbar = () => {
    return (
      <Snackbar
        sx={{ zIndex: "9999999" }}
        open={snackbarOpen}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          severity={
            snackbarMessage.includes("Error") || isValid ? "success" : "error"
          }
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    );
  };

  const sortedNamesByParts = (names: any) =>
    names.sort((a: any, b: any) => {
      const partsA = a.value.split("_");
      const partsB = b.value.split("_");

      // Compare parts one by one
      for (let i = 0; i < Math.min(partsA.length, partsB.length); i++) {
        const comparison = partsA[i].localeCompare(partsB[i]);
        if (comparison !== 0) return comparison;
      }

      return partsA.length - partsB.length; // If all parts are equal, the shorter name comes first
    });

  const dimensionArray = sortedNamesByParts(
    dimensionFromUser?.map((dime: any) => ({
      label: dime,
      value: dime,
    }))
  );

  const removeStateFromURL = (
    paramsToRemove: any,
    updatedSearchParams: any
  ) => {
    // Loop through the array and delete each param
    paramsToRemove?.map((param: any) => updatedSearchParams.delete(param));

    // Update the URL
    setSearchParams(updatedSearchParams);
  };

  const intervalList = [{ label: "Month" }, { label: "Quarter" }];

  //Quarter Definitions
  // Extend dayjs with the quarterOfYear plugin
  dayjs.extend(quarterOfYear);

  function getQuartersRange(pastQuarters: any, futureQuarters: any) {
    const quarters = [];
    const today = dayjs();

    for (let i = -pastQuarters; i <= futureQuarters; i++) {
      const date = today.add(i, "quarter"); // Now "quarter" is valid
      const year = date.year();
      const quarter = date.quarter(); // Get the quarter directly
      quarters.push({ label: `Q${quarter} ${year}` });
    }

    return [...quarters].reverse();
  }

  // Get last 8 quarters and next 2 quarters
  const quarterList = getQuartersRange(8, 2);
  const [selectedPriodeOptions, setSelectedPriodeOptions] = useState(
    quarterList
  ) as any;

  // Calculate the date range
  const minDate = dayjs().subtract(6, "month"); // 6 months back
  const maxDate = dayjs().add(2, "year"); // 2 years ahead

  const updateParamsSelectedInterval = (
    newValue: any,
    updatedSearchParams?: any
  ) => {
    setSearchParams((prevParams) => {
      let updatedParams;
      if (updatedSearchParams) {
        updatedParams = updatedSearchParams;
      } else {
        updatedParams = new URLSearchParams(prevParams); // Correctly handle params
      }
      if (newValue) {
        updatedParams.set("selectedInterval", newValue); // Set new value
      } else {
        updatedParams.delete("selectedInterval"); // Remove from URL if cleared
      }
      return updatedParams;
    });
  };

  const updateParamsSelectedPeriod = (
    newValue: any,
    updatedSearchParams?: any
  ) => {
    setSearchParams((prevParams) => {
      let updatedParams;
      if (updatedSearchParams) {
        updatedParams = updatedSearchParams;
      } else {
        updatedParams = new URLSearchParams(prevParams); // Correctly handle params
      }
      if (newValue) {
        updatedParams.set("selectedPeriod", newValue); // Set new value
      } else {
        updatedParams.delete("selectedPeriod"); // Remove from URL if cleared
      }
      return updatedParams;
    });
  };

  const updateParamsSelectedType = (
    newValue: any,
    updatedSearchParams?: any
  ) => {
    setSearchParams((prevParams) => {
      let updatedParams;
      if (updatedSearchParams) {
        updatedParams = updatedSearchParams;
      } else {
        updatedParams = new URLSearchParams(prevParams); // Correctly handle params
      }
      if (newValue) {
        updatedParams.set("selectedType", newValue); // Set new value
      } else {
        updatedParams.delete("selectedType"); // Remove from URL if cleared
      }
      return updatedParams;
    });
  };

  const updateParamsSelectedSharedUid = (
    newValue: any,
    updatedSearchParams?: any
  ) => {
    setSearchParams((prevParams) => {
      let updatedParams;
      if (updatedSearchParams) {
        updatedParams = updatedSearchParams;
      } else {
        updatedParams = new URLSearchParams(prevParams); // Correctly handle params
      }
      if (newValue) {
        const selectedIds = newValue
          .map((item: any) => (item?.value ? item?.value : item))
          .join(",");
        updatedParams.set(
          "selectedSharedUids",
          decodeURIComponent(selectedIds)
        ); // Set new value
      } else {
        updatedParams.delete("selectedSharedUids"); // Remove from URL if cleared
      }
      return updatedParams;
    });
  };

  const updateParamsSelectedDimension = (
    newValue: any,
    updatedSearchParams?: any
  ) => {
    setSearchParams((prevParams) => {
      let updatedParams;
      if (updatedSearchParams) {
        updatedParams = updatedSearchParams;
      } else {
        updatedParams = new URLSearchParams(prevParams); // Correctly handle params
      }
      if (newValue) {
        const selectedIds = newValue
          .map((item: any) =>
            item?.value || (item && !item[0]?.dimension_name)
              ? item?.value || item
              : item?.map((val: any) => val?.dimension_name)
          )
          .join(",");
        updatedParams.set("selectedDimension", decodeURIComponent(selectedIds)); // Set new value
      } else {
        updatedParams.delete("selectedDimension"); // Remove from URL if cleared
      }
      return updatedParams;
    });
  };

  const updateParamsSelectedAll = (
    newValue: any,
    updatedSearchParams?: any
  ) => {
    setSearchParams((prevParams) => {
      let updatedParams;
      if (updatedSearchParams) {
        updatedParams = updatedSearchParams;
      } else {
        updatedParams = new URLSearchParams(prevParams); // Correctly handle params
      }
      updatedParams.set("selectedAll", newValue); // Set new value
      return updatedParams;
    });
  };

  // Handle period change
  const handlePeriodChange = (newValue: any) => {
    if (newValue) {
      if (savedInterval === "Month") {
        if (dayjs(newValue).isValid()) {
          const formattedDate = dayjs(newValue)?.format("YYYY-MM");
          setSavedPeriod(formattedDate);
          updateParamsSelectedPeriod(formattedDate);
        } else {
          setSavedPeriod("");
        }
      } else {
        if (
          selectedPriodeOptions.find(
            (item: any) => item.label === newValue?.target?.textContent
          )
        ) {
          setSelectedPriodeOptions(quarterList);
          setSavedPeriod(newValue?.target?.textContent);
          updateParamsSelectedPeriod(newValue?.target?.textContent);
        } else {
          setSavedPeriod("");
        }
      }
    }
  };

  // Handle interval change
  const handleIntervalChange = (_: any, newValue: any) => {
    if (newValue?.label) {
      setSavedInterval(newValue?.label);
      updateParamsSelectedInterval(newValue?.label);

      if (newValue?.label === "Month") {
        const now = dayjs(); // Get the current date
        setSavedPeriod(now.format("YYYY-MM"));
      }
      if (newValue?.label === "Quarter") {
        setSelectedPriodeOptions(quarterList);
        const currentMonth = new Date().getMonth(); // 0-based (0 = January)
        const currentQuarter = Math.floor(currentMonth / 3) + 1;
        setSavedPeriod(quarterList[currentQuarter]?.label as any);
      }
    }
  };

  const handleSelectWebsiteOrDimension = (
    _: any,
    newValue: any,
    allSelected?: boolean
  ) => {
    const filteredValue = newValue
      ? Array.isArray(newValue)
        ? newValue.filter((item: any) => item.value !== "select_all")
        : newValue
      : "";
    let dimensionBySite = [] as any;
    let siteByDimension = [] as any;
    const newUid = filteredValue ? filteredValue : "";

    if (allSelected) {
      setSavedSelectedAll(true);
      if (savedType === "dimension" || savedType === "site") {
        const dimensionForSelectAll = dimensionArray.map(
          (item: any) => item.value
        );
        setSavedDimensions(dimensionForSelectAll);
        if (savedType === "site") {
          setSavedUid(newUid);
        } else {
          // eslint-disable-next-line
          newUid?.map((selectedDimension: any) => {
            siteByDimension.push(
              siteOptions?.find((item: any) =>
                item?.dimensions?.find(
                  (i: any) => i?.dimension_name === selectedDimension?.value
                )
              ).uid
            );
          });

          // update local states
          setSavedUid(siteByDimension);
        }
      }
    } else if (savedType === "site") {
      // eslint-disable-next-line
      newUid?.map((siteSelect: any) => {
        const dimenstionFound = siteOptions?.find(
          (item: any) => item?.uid === siteSelect?.value
        )?.dimensions;
        if (dimenstionFound) {
          dimensionBySite.push(dimenstionFound);
        }
      });

      // update local states
      setSavedUid(newUid);
      setSavedDimensions(dimensionBySite);
      setSavedSelectedAll(false);
    } else if (savedType === "dimension") {
      // eslint-disable-next-line
      newUid?.map((selectedDimension: any) => {
        siteByDimension.push(
          siteOptions?.find((item: any) =>
            item?.dimensions?.find(
              (i: any) => i?.dimension_name === selectedDimension?.value
            )
          ).uid
        );
      });

      // update local states
      setSavedUid(siteByDimension);
      setSavedDimensions(newUid);
      setSavedSelectedAll(false);
    }
  };

  const handleGetData = (
    _?: any,
    period?: any,
    interval?: any,
    sharedUids?: any,
    dimensionsfromUrl?: any,
    selectedTypefromUrl?: any,
    allSelectedUrl?: any
  ) => {
    const dates = calculateStartAndEndDates(
      period ? period : savedPeriod,
      interval ? interval : savedInterval
    );
    let twoDaysAgo = dayjs().subtract(2, "day").format("YYYY-MM-DD");

    let daysLeftForPeriod;

    // calculation how much days left for the period in quarter interval
    if (savedInterval === "Quarter") {
      daysLeftForPeriod = dayjs(dates.endDate).diff(twoDaysAgo, "days");
    } else {
      daysLeftForPeriod = dayjs(dates.startDate)
        .endOf("month")
        .diff(twoDaysAgo, "days");
    }

    if (daysLeftForPeriod < 0) {
      daysLeftForPeriod = 0;
    }

    const updatedSearchParams = new URLSearchParams(searchParams);
    if (
      (savedUid?.length > 0 || sharedUids) &&
      (dimensionsfromUrl ||
        savedDimensions?.length > 0 ||
        dimensionFromUser.length > 0) &&
      (selectedTypefromUrl || savedType) &&
      dates?.startDate &&
      dates?.endDate
    ) {
      updateParamsSelectedPeriod(
        period ? period : savedPeriod,
        updatedSearchParams
      );
      // updating local states
      setSavedPeriod(period ? period : savedPeriod);

      updateParamsSelectedInterval(
        interval ? interval : savedInterval,
        updatedSearchParams
      );
      // updating local states
      setSavedInterval(interval ? interval : savedInterval);

      updateParamsSelectedType(
        selectedTypefromUrl ? selectedTypefromUrl : savedType,
        updatedSearchParams
      );
      // updating local states
      setSavedType(selectedTypefromUrl ? selectedTypefromUrl : savedType);

      if (savedSelectedAll || allSelectedUrl === "true") {
        // updating local states
        setSavedUid(savedUid?.length > 0 ? savedUid : sharedUids);
        const isOrganization =
          (selectedTypefromUrl ? selectedTypefromUrl : savedType) ===
          "organization";
        updateParamsSelectedSharedUid(
          savedUid?.length > 0 ? savedUid : sharedUids,
          updatedSearchParams
        );
        // just for selecting all for organization need to save the dimensions since it's in another api call
        if (isOrganization) {
          // For organization, ensure URL stores all website_shared_uids
          updateParamsSelectedSharedUid(
            savedUid?.length > 0 ? savedUid : sharedUids,
            updatedSearchParams
          );
        } else {
          // previous behavior for site/dimension select all
          updateParamsSelectedDimension(
            savedDimensions?.length > 0 ? savedDimensions : dimensionsfromUrl,
            updatedSearchParams
          );
        }

        updateParamsSelectedAll(true, updatedSearchParams);
      } else {
        updateParamsSelectedSharedUid(
          savedUid?.length > 0 ? savedUid : sharedUids,
          updatedSearchParams
        );

        updateParamsSelectedDimension(
          savedDimensions?.length > 0 ? savedDimensions : dimensionsfromUrl,
          updatedSearchParams
        );

        // updating local states
        setSavedUid(savedUid?.length > 0 ? savedUid : sharedUids);

        updateParamsSelectedAll(false, updatedSearchParams);
      }

      // updating local states
      setSavedDimensions(
        savedDimensions?.length > 0 ? savedDimensions : dimensionsfromUrl
      );
      const uiType = selectedTypefromUrl ? selectedTypefromUrl : savedType;
      const effectiveType = uiType === "organization" ? "site" : uiType;
      const resolvedDimensions =
        savedDimensions?.length > 0 ? savedDimensions : dimensionsfromUrl;
      const effectiveDimensions =
        uiType === "organization"
          ? [dimensionFromUser?.[0]]
          : uiType === "site"
          ? getFirstSiteDimension(resolvedDimensions)
            ? [getFirstSiteDimension(resolvedDimensions)]
            : []
          : resolvedDimensions;
      setCollapseAllSections(true);

      // Publish the selection before firing anything - every section builds its
      // request from the store, so this is what lets them all run in parallel.
      // Only the first dimension is stored, matching the payload the sections
      // other than High Level have always sent.
      const selection: BuiDashboardSelection = {
        sharedUids: savedUid?.length > 0 ? savedUid : sharedUids,
        dimensions: effectiveDimensions?.[0] ? [effectiveDimensions[0]] : [],
        type: effectiveType,
        startDate: dates?.startDate as string,
        endDate: dates?.endDate as string,
        selectedAll: allSelectedUrl ? allSelectedUrl : savedSelectedAll,
        period: period ? period : savedPeriod,
        interval: interval ? interval : savedInterval,
        daysLeftForPeriod: daysLeftForPeriod as any,
      };
      store.dispatch(setBuiDashboardSelection(selection));

      setHighLevelData(null);
      sethighLevelTargetActualData(null);
      getAllDashboardSectionsData(selection, effectiveDimensions);
      onGetDataTriggered();
    } else {
      setSnackbarOpen(true);
      setSnackbarMessage("Missing vlaues to perform this action");
    }
  };

  const getFirstSiteDimension = (dimensions: any) => {
    const firstItem = dimensions?.[0];

    if (Array.isArray(firstItem)) {
      return firstItem?.[0];
    }

    return firstItem;
  };

  const onLoadCheck = () => {
    const searchParamsSelectedType = searchParams.get("selectedType");
    const searchParamsSelectedAll = searchParams.get("selectedAll");
    //const selectedDimensionURLFromAll = searchParams.get("selectedDimension");

    let selectedDimensionByURL;
    let selectedShareUIDFromUrl;

    if (searchParamsSelectedAll === "true") {
      setSavedSelectedAll(true);
      if (
        searchParamsSelectedType === "dimension" ||
        searchParamsSelectedType === "site"
      ) {
        // if user choose selected all on dimension or website need to take the all dimensions from user's permission
        selectedDimensionByURL = dimensionArray?.map((item: any) => item.value);
        selectedShareUIDFromUrl = websiteList;
      } else {
        const sharedUidsParam = searchParams.get("selectedSharedUids");
        selectedShareUIDFromUrl = sharedUidsParam
          ? decodeURIComponent(sharedUidsParam)
              .split(",")
              .map(
                (uid: string) =>
                  websiteList.find((w: any) => w.value === uid) || {
                    label: uid,
                    value: uid,
                  }
              )
          : [websiteList[0]];
        // dimensions can be derived from websites if needed, but we don't store them in URL anymore
        selectedDimensionByURL = []; // keep empty or set from viewOrganizationalSummary if needed
      }
    } else {
      // updating choosen dimensions
      selectedDimensionByURL = decodeURIComponent(
        searchParams.get("selectedDimension") as any
      )
        .split(",")
        .map((value: any) => {
          return (
            dimensionArray?.find((item: any) => item.value === value) || null
          );
        });

      // updating choosen sties
      selectedShareUIDFromUrl = decodeURIComponent(
        searchParams.get("selectedSharedUids") as any
      )
        .split(",")
        .map((value: any) => {
          return websiteList.find((item: any) => item.value === value) || null;
        });
    }

    // updating radio buttons
    if (searchParamsSelectedType === "dimension") {
      setSelectedValueRadioFilter("Dimension");
    } else if (searchParamsSelectedType === "organization") {
      setSelectedValueRadioFilter("Organization");
      setSavedUid(selectedShareUIDFromUrl);
      setWebsiteList(selectedShareUIDFromUrl);
    } else {
      setSelectedValueRadioFilter("Website");
    }

    if (searchParamsSelectedAll === "true") {
      setSavedSelectedAll(true);
    }

    if (
      (searchParams.get("selectedPeriod") &&
        searchParams.get("selectedInterval") &&
        searchParams.get("selectedType") &&
        selectedDimensionByURL?.some((item: any) => item !== null) &&
        selectedShareUIDFromUrl?.some((item: any) => item !== null),
      searchParamsSelectedAll)
    ) {
      handleGetData(
        "",
        searchParams.get("selectedPeriod") as any,
        searchParams.get("selectedInterval") as any,
        selectedShareUIDFromUrl,
        selectedDimensionByURL,
        searchParams.get("selectedType") as any,
        searchParamsSelectedAll
      );
    } else {
      const now = dayjs(); // Get the current date
      setSavedPeriod(now.format("YYYY-MM"));
      setSavedInterval("Month");
      setSearchParams({});
    }
  };

  useEffect(() => {
    const sp = Object.fromEntries(searchParams.entries());
    const hasPeriod = !!sp.selectedPeriod;
    const hasInterval = !!sp.selectedInterval;
    const hasType = !!sp.selectedType;
    const hasSelectedAll = typeof sp.selectedAll !== "undefined";
    const hasSharedUids = !!sp.selectedSharedUids;
    const hasDimension = !!sp.selectedDimension;

    const urlLooksValidForOrg =
      hasPeriod &&
      hasInterval &&
      hasType &&
      hasSelectedAll &&
      hasSharedUids &&
      sp.selectedType === "organization";

    const urlLooksValidDefault =
      hasPeriod &&
      hasInterval &&
      hasType &&
      hasSelectedAll &&
      hasSharedUids &&
      hasDimension;

    if (
      urlLooksValidForOrg ||
      urlLooksValidDefault ||
      (selectedType &&
        selectedInterval &&
        selectedAll?.toString()?.length > 0 &&
        selectedDimensions &&
        selectedSharedUidBui)
    ) {
      if (
        (sp.selectedType === "organization" &&
          !isUserHasViewOrganizationalSummary) ||
        (sp.selectedType === "organization" && !sp.selectedSharedUids)
      ) {
        // user doesn't have permission for organization or missing required params
        const now = dayjs();
        setSavedPeriod(now.format("YYYY-MM"));
        setSavedInterval("Month");
        setSearchParams({});
      } else {
        onLoadCheck();
      }
    } else {
      const now = dayjs();
      setSavedPeriod(now.format("YYYY-MM"));
      setSavedInterval("Month");
      setSearchParams({});
    }

    if (isUserHasViewOrganizationalSummary) {
      getAllDimesnionsForViewOrganizationalSummary(
        setViewOrganizationalSummary
      );
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (viewOrganizationalSummary?.length > 0) {
      const newObj = viewOrganizationalSummary
        .filter(
          (obj: any) =>
            "dimension" in obj &&
            obj.organization_id__organization_name ===
              userData?.data?.user_details?.organization_name
        )
        .map((obj: { dimension: any }) => obj.dimension);

      setAllDimensionOrganization(newObj);
    }
    // eslint-disable-next-line
  }, [viewOrganizationalSummary]);

  const getValueForDimensionOrWebiste = () => {
    if (selectedValueRadioFilter.toLowerCase() === "organization") {
      return [];
    } else {
      if (savedSelectedAll) {
        if (savedType.toLowerCase() === "site") {
          return websiteList;
        } else {
          return dimensionList;
        }
      } else {
        if (savedType.toLowerCase() === "site") {
          return Array.isArray(savedUid)
            ? savedUid.filter((item: any) => item.value !== "select_all")
            : [];
        } else {
          return Array.isArray(savedDimensions)
            ? savedDimensions.filter((item: any) => item.value !== "select_all")
            : [];
        }
      }
    }
  };
  const SELECT_ALL_OPTION = { label: "Select All", value: "select_all" };

  const isGetDataParamsValid = () => {
    if (
      savedUid?.length === 0 ||
      savedUid === null ||
      savedInterval?.length === 0 ||
      savedInterval === null ||
      savedPeriod?.length === 0 ||
      savedPeriod === null
    ) {
      return false;
    } else {
      return true;
    }
  };

  const resetSelections = () => {
    const updatedSearchParams = new URLSearchParams(searchParams);

    setSavedDimensions([]);
    setSavedUid([]);
    setSavedSelectedAll(false);
    setDataShow(false);

    removeStateFromURL(
      ["selectedSharedUids", "selectedDimension", "selectedAll"],
      updatedSearchParams
    );
  };

  return (
    <>
      {renderSnackbar()}
      <>
        <Paper sx={{ padding: "8px 16px", mb: 2 }}>
          <Stack
            flexDirection={"row"}
            justifyContent={{ xs: "center", md: "space-between" }}
            alignItems="center"
            width={"100%"}
          >
            <Box width={"100%"}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent={"space-between"}
              >
                <Typography fontSize={24}>Business Leader Dashboard</Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <FormLabel
                    color="secondary"
                    id="demo-row-radio-buttons-group-label"
                  >
                    <Typography fontSize={16} color="#000000DE" marginRight={1}>
                      Data By:
                    </Typography>
                  </FormLabel>
                  <RadioGroup
                    value={selectedValueRadioFilter}
                    onChange={handleChangeRadioFilter}
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                  >
                    <FormControlLabel
                      value="Website"
                      control={
                        <Radio
                          color="secondary"
                          size="small"
                          defaultChecked
                          inputProps={
                            { "data-tracker": "273" } as Record<string, any>
                          }
                        />
                      }
                      label="Website"
                      onChange={handleRadioFilterChange}
                    />
                    <FormControlLabel
                      value="Dimension"
                      control={
                        <Radio
                          color="secondary"
                          size="small"
                          inputProps={
                            { "data-tracker": "293" } as Record<string, any>
                          }
                        />
                      }
                      label="Dimension"
                      onChange={handleRadioFilterChange}
                    />
                    {isUserHasViewOrganizationalSummary ? (
                      <FormControlLabel
                        value="Organization"
                        control={
                          <Radio
                            color="secondary"
                            size="small"
                            inputProps={
                              { "data-tracker": "294" } as Record<string, any>
                            }
                          />
                        }
                        label="All Active Websites"
                        onChange={handleRadioFilterChange}
                      />
                    ) : (
                      ""
                    )}
                  </RadioGroup>
                </Stack>
              </Stack>
              <Typography sx={{ color: "#00000099", paddingBottom: "16px" }}>
                Concise, data-rich overview of critical analytics, equipping you
                with the insights needed to make informed and evidence-based
                decisions.
              </Typography>
            </Box>
          </Stack>
          <Stack mt={0} mb={1} flexDirection={"row"} gap={2}>
            <Stack width={"80%"}>
              <Box sx={{ flexShrink: 0, minWidth: "250px" }}>
                <Autocomplete
                  disabled={
                    selectedValueRadioFilter === "Organization" ? true : false
                  }
                  multiple
                  options={
                    selectedValueRadioFilter === "Dimension"
                      ? savedSelectedAll
                        ? dimensionArray // Don't show "Select All" when all are selected
                        : [SELECT_ALL_OPTION, ...dimensionArray]
                      : savedSelectedAll
                      ? websiteList // Don't show "Select All" when all are selected
                      : [SELECT_ALL_OPTION, ...websiteList]
                  }
                  getOptionLabel={(option: any) => option.label}
                  isOptionEqualToValue={(option: any, value: any) =>
                    option?.value === value?.value
                  }
                  filterSelectedOptions
                  size="small"
                  value={getValueForDimensionOrWebiste()}
                  onChange={(event: any, newValue: any, reason: any) => {
                    const isClearAction =
                      reason === "clear" ||
                      (Array.isArray(newValue) && newValue.length === 0) ||
                      newValue === "";

                    if (isClearAction) {
                      // clear UI and URL same as when switching filter
                      resetSelections();
                      return;
                    }
                    const optionsList =
                      selectedValueRadioFilter === "Dimension"
                        ? dimensionArray
                        : websiteList;

                    // Handle select all
                    if (
                      newValue.find(
                        (item: any) => item.value === "select_all"
                      ) &&
                      reason === "selectOption"
                    ) {
                      handleSelectWebsiteOrDimension(event, optionsList, true); // select all options
                    } else {
                      handleSelectWebsiteOrDimension(event, newValue);
                    }

                    setDataShow(false);
                  }}
                  renderTags={(value, getTagProps) =>
                    value?.map((option: any, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option?.value + "." + Math.random()}
                        label={option?.label}
                        sx={{
                          backgroundColor: "#7B1FA2",
                          color: "#ffffff",
                          height: "24px",
                        }}
                        deleteIcon={
                          <CancelIcon
                            sx={{
                              color: "rgba(255, 255, 255, 0.8) !important",
                              fontSize: "16px !important",
                            }}
                          />
                        }
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        selectedValueRadioFilter === "Organization"
                          ? "All Active Websites"
                          : "Search site name or choose from the list"
                      }
                      label="Select Website or Dimension"
                      color="secondary"
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={{
                        backgroundColor:
                          option.value === "select_all"
                            ? "#9C27B00A"
                            : "inherit",
                        color:
                          option.value === "select_all" ? "#9C27B0" : "inherit",
                        fontWeight:
                          option.value === "select_all" ? "500" : "normal",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#9C27B00A";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                          option.value === "select_all"
                            ? "#9C27B00A"
                            : "inherit";
                      }}
                    >
                      {option.label}
                    </li>
                  )}
                />
              </Box>
            </Stack>
            <Stack width={"20%"}>
              <Autocomplete
                options={intervalList}
                value={savedInterval as any}
                onChange={handleIntervalChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Interval"
                    label="Time Interval"
                    color="secondary"
                  />
                )}
                size="small"
              />
            </Stack>
            {savedInterval === "Month" ? (
              <Stack sx={{ mt: "-8px" }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={["DatePicker"]}>
                    <DatePicker
                      minDate={minDate}
                      maxDate={maxDate}
                      views={["year", "month"]}
                      slotProps={{
                        textField: {
                          size: "small",
                          color: "secondary",
                          required: true,
                          fullWidth: true,
                        },
                      }}
                      format="MM/YYYY"
                      value={dayjs(savedPeriod)}
                      sx={{ width: "100%", minWidth: "unset !important" }}
                      label="Time Period"
                      onChange={(e) => handlePeriodChange(e)}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Stack>
            ) : (
              <Stack width={"20%"}>
                <Autocomplete
                  options={selectedPriodeOptions}
                  value={savedPeriod}
                  onChange={(e) => handlePeriodChange(e)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      fullWidth
                      placeholder="Select Period"
                      label="Time period"
                      color="secondary"
                      sx={{ width: "100%", minWidth: "unset !important" }}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                    />
                  )}
                  size="small"
                />
              </Stack>
            )}
            <Stack>
              <Stack spacing={2} alignItems={"flex-end"}>
                <Button
                  data-tracker="56"
                  disabled={isGetDataParamsValid() ? false : true}
                  onClick={handleGetData}
                  variant="contained"
                  fullWidth
                  size="large"
                  color="secondary"
                  sx={{ fontSize: "15px", width: "116px" }}
                >
                  Get Data
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </>
    </>
  );
}

export default BusinessLeaderDashboardFilter;
